/**
 * Tadika games: Kira Gambar (preschool maths), Celik Jawi, Mewarna (colouring).
 * All text is original. Jawi spellings are standard everyday words; see JAWI_NEEDS_REVIEW.
 */
import { sainsTexts } from "./sainsInteraktif.js";


/* ── Kira Gambar ─────────────────────────────────────────────────────────── */
/** penjodoh bilangan (classifier) matters in Malay: "Berapa ekor ikan?" not "Berapa ikan?" */
export type KiraItem = { name: string; e: string; pj: "ekor" | "biji" | "buah" | "kuntum" | "batang"; /** English version: plural for the question, art id of the picture */ plural?: string; art?: string };
export const KIRA_ITEMS: KiraItem[] = [
  { name: "ikan", e: "🐟", pj: "ekor" },
  { name: "ayam", e: "🐔", pj: "ekor" },
  { name: "kucing", e: "🐱", pj: "ekor" },
  { name: "itik", e: "🦆", pj: "ekor" },
  { name: "rama-rama", e: "🦋", pj: "ekor" },
  { name: "epal", e: "🍎", pj: "biji" },
  { name: "telur", e: "🥚", pj: "biji" },
  { name: "bola", e: "⚽", pj: "biji" },
  { name: "bunga", e: "🌸", pj: "kuntum" },
  { name: "kereta", e: "🚗", pj: "buah" },
  { name: "rumah", e: "🏠", pj: "buah" },
  { name: "buku", e: "📘", pj: "buah" },
  { name: "pensel", e: "✏️", pj: "batang" },
];
export const NOMBOR = ["kosong", "satu", "dua", "tiga", "empat", "lima", "enam", "tujuh", "lapan", "sembilan", "sepuluh"];
export const kiraQuestion = (it: KiraItem) => (it.plural ? `How many ${it.plural}?` : `Berapa ${it.pj} ${it.name}?`);
export const tambahQuestion = (it: KiraItem) => (it.plural ? `How many ${it.plural} altogether?` : `Berapa ${it.pj} ${it.name} semuanya?`);

export type KiraRound = { item: KiraItem; a: number; b: number; answer: number; options: number[]; mode: "kira" | "tambah" };

/** Four different whole numbers including the answer, 1..max, shuffled with `rnd`. */
export function kiraOptions(answer: number, max: number, rnd: () => number = Math.random): number[] {
  const opts = new Set([answer]);
  const near = [answer - 1, answer + 1, answer - 2, answer + 2, answer + 3, answer - 3].filter((n) => n >= 1 && n <= max);
  for (const n of near) { if (opts.size >= 4) break; if (rnd() < 0.8) opts.add(n); }
  for (let guard = 0; opts.size < 4 && guard < 100; guard++) opts.add(1 + Math.floor(rnd() * max));
  return [...opts].sort(() => rnd() - 0.5);
}

/** stage 1: count 1–5, stage 2: count 1–10, stage 3: add two groups (total ≤ 10). */
export function kiraRound(stage: 1 | 2 | 3, rnd: () => number = Math.random, items: KiraItem[] = KIRA_ITEMS): KiraRound {
  const item = items[Math.floor(rnd() * items.length)]!;
  if (stage === 3) {
    const a = 1 + Math.floor(rnd() * 5);
    const b = 1 + Math.floor(rnd() * Math.min(5, 10 - a));
    return { item, a, b, answer: a + b, options: kiraOptions(a + b, 10, rnd), mode: "tambah" };
  }
  const max = stage === 1 ? 5 : 10;
  const n = 1 + Math.floor(rnd() * max);
  return { item, a: n, b: 0, answer: n, options: kiraOptions(n, max, rnd), mode: "kira" };
}

/* ── Celik Jawi ──────────────────────────────────────────────────────────── */
export type JawiLetter = { j: string; name: string; sound: string; quiz: boolean };
/** 36 Jawi entries, ordered right-to-left by rows exactly as in the supplied Belajar Huruf JAWI poster. */
export const JAWI_LETTERS: JawiLetter[] = [
  { j: "ا", name: "alif", sound: "a", quiz: true },
  { j: "ب", name: "ba", sound: "b", quiz: true },
  { j: "ت", name: "ta", sound: "t", quiz: true },
  { j: "ث", name: "sa", sound: "th", quiz: true },
  { j: "ج", name: "jim", sound: "j", quiz: true },
  { j: "ح", name: "ha", sound: "ḥ", quiz: true },
  { j: "خ", name: "kho", sound: "kh", quiz: true },
  { j: "چ", name: "ca", sound: "ch", quiz: true },
  { j: "د", name: "dal", sound: "d", quiz: true },
  { j: "ذ", name: "zal", sound: "dh", quiz: true },
  { j: "ر", name: "ro", sound: "r", quiz: true },
  { j: "ز", name: "zai", sound: "z", quiz: true },
  { j: "س", name: "sin", sound: "s", quiz: true },
  { j: "ش", name: "syin", sound: "sy", quiz: true },
  { j: "ص", name: "sod", sound: "ṣ", quiz: true },
  { j: "ض", name: "dod", sound: "ḍ", quiz: true },
  { j: "ط", name: "to", sound: "ṭ", quiz: true },
  { j: "ظ", name: "zo", sound: "ẓ", quiz: true },
  { j: "ع", name: "ain", sound: "ʕ", quiz: true },
  { j: "غ", name: "ghain", sound: "gh", quiz: true },
  { j: "ڠ", name: "nga", sound: "ng", quiz: true },
  { j: "ف", name: "fa", sound: "f", quiz: true },
  { j: "ڤ", name: "pa", sound: "p", quiz: true },
  { j: "ق", name: "qaf", sound: "q", quiz: true },
  { j: "ک", name: "kaf", sound: "k", quiz: true },
  { j: "ݢ", name: "ga", sound: "g", quiz: true },
  { j: "ل", name: "lam", sound: "l", quiz: true },
  { j: "م", name: "mim", sound: "m", quiz: true },
  { j: "ن", name: "nun", sound: "n", quiz: true },
  { j: "و", name: "wau", sound: "w", quiz: true },
  { j: "ۏ", name: "va", sound: "v", quiz: true },
  { j: "ه", name: "ha", sound: "h", quiz: true },
  { j: "لا", name: "lam-alif", sound: "la", quiz: true },
  { j: "ء", name: "hamzah", sound: "ʔ", quiz: true },
  { j: "ي", name: "ya", sound: "y", quiz: true },
  { j: "ڽ", name: "nya", sound: "ny", quiz: true },
];

/** Simple picture words in Jawi and Rumi (everyday spellings). */
/** Quiz distractors have distinct displayed names: the two ha identities must never duplicate answer labels. */
export function jawiAnswerPool(letter: JawiLetter): JawiLetter[] {
  return [...new Map(JAWI_LETTERS.filter(l => l.quiz && l.name !== letter.name).map(l => [l.name, l])).values()];
}

export type JawiWord = { jawi: string; rumi: string; e: string };
export const JAWI_WORDS: JawiWord[] = [
  { jawi: "ايبو", rumi: "ibu", e: "👩" },
  { jawi: "باجو", rumi: "baju", e: "👕" },
  { jawi: "بولا", rumi: "bola", e: "⚽" },
  { jawi: "ايکن", rumi: "ikan", e: "🐟" },
  { jawi: "روتي", rumi: "roti", e: "🍞" },
  { jawi: "سوسو", rumi: "susu", e: "🥛" },
  { jawi: "توڤي", rumi: "topi", e: "🧢" },
  { jawi: "کوچيڠ", rumi: "kucing", e: "🐱" },
  { jawi: "ڤيسڠ", rumi: "pisang", e: "🍌" },
  { jawi: "ݢيݢي", rumi: "gigi", e: "🦷" },
  { jawi: "ڽاموق", rumi: "nyamuk", e: "🦟" },
  { jawi: "لمبو", rumi: "lembu", e: "🐄" },
];
/** Spellings a Jawi teacher should confirm before launch. */
export const JAWI_NEEDS_REVIEW = JAWI_WORDS.map((w) => `${w.rumi} = ${w.jawi}`);

/* ── Mewarna ─────────────────────────────────────────────────────────────── */
export const WARNA = [
  { name: "merah", hex: "#EF4444" }, { name: "oren", hex: "#F97316" }, { name: "kuning", hex: "#FACC15" },
  { name: "hijau", hex: "#22C55E" }, { name: "biru", hex: "#3B82F6" }, { name: "ungu", hex: "#A855F7" },
  { name: "merah jambu", hex: "#F472B6" }, { name: "coklat", hex: "#92400E" }, { name: "kelabu", hex: "#9CA3AF" },
  { name: "hitam", hex: "#1F2937" }, { name: "putih", hex: "#FFFFFF" }, { name: "biru muda", hex: "#7DD3FC" },
];

/** Line drawings for colouring: each part is a closed SVG path the child can fill. viewBox 0 0 200 200. */
export type ColorPicture = { id: string; name: string; e: string; parts: { id: string; d: string }[]; lines?: string[] };
export const GAMBAR_WARNA: ColorPicture[] = [
  {
    id: "ikan", name: "Ikan", e: "🐟",
    parts: [
      { id: "badan", d: "M40 100 C60 55 130 55 150 100 C130 145 60 145 40 100 Z" },
      { id: "ekor", d: "M150 100 L185 70 L178 100 L185 130 Z" },
      { id: "sirip", d: "M85 68 C95 45 115 45 120 66 Z" },
      { id: "mata", d: "M62 92 a8 8 0 1 0 16 0 a8 8 0 1 0 -16 0 Z" },
      { id: "air", d: "M0 160 C30 150 60 170 100 160 C140 150 170 170 200 160 L200 200 L0 200 Z" },
    ],
    lines: ["M95 80 C105 95 105 105 95 120", "M115 80 C125 95 125 105 115 120"],
  },
  {
    id: "rumah", name: "Rumah", e: "🏠",
    parts: [
      { id: "bumbung", d: "M30 95 L100 35 L170 95 Z" },
      { id: "dinding", d: "M45 95 L155 95 L155 170 L45 170 Z" },
      { id: "pintu", d: "M88 120 L112 120 L112 170 L88 170 Z" },
      { id: "tingkap1", d: "M55 110 L78 110 L78 133 L55 133 Z" },
      { id: "tingkap2", d: "M122 110 L145 110 L145 133 L122 133 Z" },
      { id: "matahari", d: "M160 30 a14 14 0 1 0 28 0 a14 14 0 1 0 -28 0 Z" },
      { id: "rumput", d: "M0 170 L200 170 L200 200 L0 200 Z" },
    ],
  },
  {
    id: "bunga", name: "Bunga", e: "🌸",
    parts: [
      { id: "kelopak1", d: "M86 40 a14 26 0 1 0 28 0 a14 26 0 1 0 -28 0 Z" },
      { id: "kelopak2", d: "M104 70 a26 14 0 1 0 52 0 a26 14 0 1 0 -52 0 Z" },
      { id: "kelopak3", d: "M86 100 a14 26 0 1 0 28 0 a14 26 0 1 0 -28 0 Z" },
      { id: "kelopak4", d: "M44 70 a26 14 0 1 0 52 0 a26 14 0 1 0 -52 0 Z" },
      { id: "tengah", d: "M88 70 a12 12 0 1 0 24 0 a12 12 0 1 0 -24 0 Z" },
      { id: "batang", d: "M96 126 L104 126 L104 175 L96 175 Z" },
      { id: "daun", d: "M104 140 C125 120 150 125 155 135 C140 150 120 150 104 145 Z" },
      { id: "pasu", d: "M70 170 L130 170 L120 198 L80 198 Z" },
    ],
  },
  {
    id: "kereta", name: "Kereta", e: "🚗",
    parts: [
      { id: "badan", d: "M20 120 L40 120 L60 85 L140 85 L160 120 L180 120 L180 150 L20 150 Z" },
      { id: "cermin1", d: "M68 92 L98 92 L98 118 L52 118 Z" },
      { id: "cermin2", d: "M104 92 L134 92 L150 118 L104 118 Z" },
      { id: "tayar1", d: "M45 150 a17 17 0 1 0 34 0 a17 17 0 1 0 -34 0 Z" },
      { id: "tayar2", d: "M121 150 a17 17 0 1 0 34 0 a17 17 0 1 0 -34 0 Z" },
      { id: "lampu", d: "M170 125 L180 125 L180 135 L170 135 Z" },
      { id: "jalan", d: "M0 168 L200 168 L200 200 L0 200 Z" },
    ],
  },
  {
    id: "kucing", name: "Kucing", e: "🐱",
    parts: [
      { id: "telinga1", d: "M55 75 L65 30 L90 60 Z" },
      { id: "telinga2", d: "M145 75 L135 30 L110 60 Z" },
      { id: "muka", d: "M40 105 C40 60 160 60 160 105 C160 150 40 150 40 105 Z" },
      { id: "mata1", d: "M70 95 a9 11 0 1 0 18 0 a9 11 0 1 0 -18 0 Z" },
      { id: "mata2", d: "M112 95 a9 11 0 1 0 18 0 a9 11 0 1 0 -18 0 Z" },
      { id: "hidung", d: "M93 112 L107 112 L100 121 Z" },
      { id: "reben", d: "M80 150 L100 160 L120 150 L120 172 L100 162 L80 172 Z" },
    ],
    lines: ["M100 121 C95 130 85 130 82 126", "M100 121 C105 130 115 130 118 126", "M60 115 L25 108", "M60 122 L25 126", "M140 115 L175 108", "M140 122 L175 126"],
  },
  {
    id: "pelangi", name: "Pelangi", e: "🌈",
    parts: [
      { id: "lengkung1", d: "M20 150 A80 80 0 0 1 180 150 L165 150 A65 65 0 0 0 35 150 Z" },
      { id: "lengkung2", d: "M35 150 A65 65 0 0 1 165 150 L150 150 A50 50 0 0 0 50 150 Z" },
      { id: "lengkung3", d: "M50 150 A50 50 0 0 1 150 150 L135 150 A35 35 0 0 0 65 150 Z" },
      { id: "awan1", d: "M10 160 C10 140 40 140 45 152 C60 145 75 160 65 172 L15 172 C5 172 5 162 10 160 Z" },
      { id: "awan2", d: "M135 160 C135 140 165 140 170 152 C185 145 200 160 190 172 L140 172 C130 172 130 162 135 160 Z" },
      { id: "matahari", d: "M150 30 a16 16 0 1 0 32 0 a16 16 0 1 0 -32 0 Z" },
    ],
  },
];

/** Texts the games read aloud (for pre-generated audio). */
export function tadikaGameTexts(): { kind: "word" | "line"; text: string }[] {
  const out: { kind: "word" | "line"; text: string }[] = [];
  for (const it of KIRA_ITEMS) { out.push({ kind: "line", text: kiraQuestion(it) }, { kind: "line", text: tambahQuestion(it) }); }
  for (const n of NOMBOR.slice(1)) out.push({ kind: "word", text: n });
  out.push({ kind: "line", text: "Betul! Pandai!" }, { kind: "line", text: "Cuba lagi." }, { kind: "line", text: "Huruf apakah ini?" }, { kind: "line", text: "Cari huruf ini." });
  for (const w of WARNA) out.push({ kind: "word", text: w.name });
  // Letter audio is identity-specific, provided by jawiClip rather than Malay word clips.
  for (const w of JAWI_WORDS) out.push({ kind: "word", text: w.rumi });
  out.push(...sainsTexts()); // Sains games: Perjalanan Makanan, Sistem Suria
  return out;
}

